import { getApi } from '../apiClient';
import { ILearningStats, ITeamMembership } from '../../models/profile';

import {
  ICurrentLicenseActivatePutResponse,
  ILicenseTeamStudent,
  IProfileClaimRequest,
  IProfileGetResponse,
  IProfilePutRequest,
  IProfilePutResponse,
  IStudentIdentity,
  IStudentLicense,
  IStudentSupportLanguagePostRequest,
  IStudentSupportLanguagePostResponse,
  IStudentSupportLanguagePutRequest,
  IStudentSupportLanguagePutResponse,
  IStudentTargetLanguagePostRequest,
  IStudentTargetLanguagePostResponse,
  IStudentTargetLanguagePutRequest,
  IStudentTargetLanguagePutResponse
} from './types';

export async function getProfile(): Promise<IProfileGetResponse> {
  const { data } = await getApi().get<IProfileGetResponse>(`/profile`);
  return data;
}

export async function getTeamMemberships(): Promise<ITeamMembership[]> {
  const { data } = await getApi().get<{ data: ITeamMembership[] }>(
    `/profile/team_memberships`
  );
  return data.data;
}

export async function updateProfile(
  profileData: IProfilePutRequest
): Promise<IProfilePutResponse> {
  const { data } = await getApi().put<IProfilePutResponse>(`/profile`, {
    student: profileData
  });

  return data;
}

export async function requestClaimProfile(email: string): Promise<void> {
  await getApi().post<IProfileClaimRequest>(`/profile/request_claim`, {
    claim: { email }
  });
}

export async function claimProfile(
  claim: IProfileClaimRequest
): Promise<IProfilePutRequest> {
  const { data } = await getApi().post<IProfileClaimRequest>(`/profile/claim`, {
    claim
  });

  return data;
}

export async function createStudentTargetLanguage(
  studentTargetLanguage: IStudentTargetLanguagePostRequest['studentTargetLanguage']
): Promise<IStudentTargetLanguagePostResponse> {
  const { data } = await getApi().post<IStudentTargetLanguagePostResponse>(
    `/profile/student_target_languages`,
    {
      studentTargetLanguage
    }
  );

  return data;
}

export async function updateStudentTargetLanguage(
  studentTargetLanguage: IStudentTargetLanguagePutRequest['studentTargetLanguage']
): Promise<IStudentTargetLanguagePutResponse> {
  const { data } = await getApi().put<IStudentTargetLanguagePutResponse>(
    studentTargetLanguage.url,
    {
      studentTargetLanguage
    }
  );

  return data;
}

export async function createStudentSupportLanguage(
  studentSupportLanguage: IStudentSupportLanguagePostRequest['studentSupportLanguage']
): Promise<IStudentSupportLanguagePostResponse> {
  const { data } = await getApi().post<IStudentSupportLanguagePostResponse>(
    `/profile/student_support_languages`,
    {
      studentSupportLanguage
    }
  );

  return data;
}

export async function updateStudentSupportLanguage(
  studentSupportLanguage: IStudentSupportLanguagePutRequest['studentSupportLanguage']
): Promise<IStudentSupportLanguagePutResponse> {
  const { data } = await getApi().put<IStudentSupportLanguagePutResponse>(
    studentSupportLanguage.url,
    {
      studentSupportLanguage
    }
  );

  return data;
}

export async function deleteProfileLanguageByUrl(languageUrl: string): Promise<void> {
  const { data } = await getApi().delete(languageUrl);

  return data;
}

export async function disconnectStudentFromSocialAccount(
  studentIdentityId: IStudentIdentity['id']
): Promise<void> {
  await getApi().delete(`/profile/student_identities/${studentIdentityId}`);
}

export async function deleteStudentLicense(
  teamStudentId: ILicenseTeamStudent['id']
): Promise<void> {
  await getApi().delete(`/profile/team_students/${teamStudentId}`);
}

export async function changeCurrentLicence(
  licenceId: IStudentLicense['id']
): Promise<ICurrentLicenseActivatePutResponse['license']> {
  const resp = await getApi().put<ICurrentLicenseActivatePutResponse>(
    `/profile/licenses/${licenceId}/activate`
  );

  return resp.data.license;
}

export async function deleteProfile(): Promise<void> {
  await getApi().delete('/profile');
}

export async function restoreProfile(): Promise<void> {
  await getApi().post('/profile/restore');
}

export async function getExportRequests(): Promise<void> {
  await getApi().get('/profile/export_data_requests');
}

export async function exportData(): Promise<void> {
  await getApi().post('/profile/export_data_requests');
}

export async function getProfileLearningProgress({
  from,
  to
}: {
  from: string;
  to: string;
}): Promise<any> {
  const { data } = await getApi().get('/profile/learning_progress', {
    params: {
      from,
      to
    }
  });

  return data;
}

export async function getNewProfileLearningProgress({
  start_date,
  end_date
}: {
  start_date?: string;
  end_date?: string;
}): Promise<any> {
  const { data } = await getApi().get('/profile/activities/chart', {
    params:
      start_date && end_date
        ? {
            start_date,
            end_date
          }
        : undefined
  });

  return data;
}

export async function getProfileStats({
  start_date,
  end_date
}: {
  start_date?: string;
  end_date?: string;
} = {}): Promise<{ data: ILearningStats }> {
  const { data } = await getApi().get<{ data: ILearningStats }>('/profile/stats', {
    params: {
      ...(start_date && { start_date }),
      ...(end_date && { end_date })
    }
  });

  return data;
}
